import { useAuth, OrgRole, SystemRole, OPS_INCLUDED } from '_/contexts/AuthContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProjectSafe } from '_/contexts/ProjectContext';

/**
 * Hook that provides permission checking for the currently selected organization.
 * Automatically uses the selected org from OrganizationContext.
 */
export function useOrgPermissions() {
  const auth = useAuth();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id || '';

  return {
    // Direct role checks for current org
    isAdmin: auth.isOrgAdmin(orgId),
    isOps: auth.isOrgOps(orgId),
    isSchemaAdmin: auth.isSchemaAdmin(orgId),
    isMember: auth.isMember(orgId),
    isSourceEngineer: auth.isSourceEngineer(orgId),

    // Get all roles for current org
    roles: auth.getOrgRoles(orgId),

    // Check if user has a specific role in current org
    hasRole: (role: string) => auth.hasOrgRole(orgId, role),

    // Check if user has any of the specified roles in current org
    hasAnyRole: (roles: string[]) => auth.hasAnyOrgRole(orgId, roles),

    // Check access based on allowed roles
    checkAccess: (allowedRoles: string | string[]) => auth.checkOrgAccess(orgId, allowedRoles),
  };
}

/**
 * Hook that provides permission checking including project lock status.
 * Combines org permissions with project lock access.
 * Safe to use outside ProjectProvider - will skip project lock check if no project context.
 */
export function useOrgAccessWithProjectLock() {
  const auth = useAuth();
  const { selectedOrganization } = useOrganization();
  const projectContext = useProjectSafe();
  const selectedProject = projectContext?.selectedProject;
  const orgId = selectedOrganization?.id || '';

  /**
   * Check if user has access considering project lock status.
   * If project is locked, only OPS and ADMIN roles have access.
   * Super admin always has access.
   */
  const checkAccessWithProjectLock = (allowedRoles: string | string[]): boolean => {
    // First check org access
    const hasOrgAccess = auth.checkOrgAccess(orgId, allowedRoles);

    // If no project is selected or project is not locked, return org access
    if (!selectedProject || !selectedProject.locked) {
      return hasOrgAccess;
    }

    // Project is locked - check if user has OPS_INCLUDED roles
    const hasLockedProjectAccess = auth.checkOrgAccess(orgId, OPS_INCLUDED);

    return hasOrgAccess && hasLockedProjectAccess;
  };

  return {
    checkAccess: checkAccessWithProjectLock,
    isProjectLocked: selectedProject?.locked || false,
  };
}

/**
 * Hook that checks if user has org access (replaces useOrgAccess hook).
 * Automatically uses the selected org from OrganizationContext.
 *
 * @param allowedRoles - Role or array of roles to check
 * @returns boolean indicating if user has access
 */
export function useOrgAccess(allowedRoles: string | string[]): boolean {
  const { checkAccess } = useOrgAccessWithProjectLock();
  return checkAccess(allowedRoles);
}

/**
 * Hook that checks if project is locked and if user has access.
 * Returns true if:
 * - Project is not locked, OR
 * - Project is locked AND user has OPS_INCLUDED roles, OR
 * - User is super admin
 * Safe to use outside ProjectProvider - will return true if no project context.
 */
export function useProjectLockAccess(): boolean {
  const auth = useAuth();
  const { selectedOrganization } = useOrganization();
  const projectContext = useProjectSafe();
  const selectedProject = projectContext?.selectedProject;

  // If super admin, always return true
  if (auth.isSuperAdmin) {
    return true;
  }

  // If no project is selected or project is not locked, return true
  if (!selectedProject || !selectedProject.locked) {
    return true;
  }

  // Project is locked - check if user has OPS_INCLUDED roles
  const orgId = selectedOrganization?.id || '';
  return auth.hasAnyOrgRole(orgId, OPS_INCLUDED);
}

// Export role constants for convenience
export { OrgRole, SystemRole, OPS_INCLUDED };
export { MEMBER_NOT_INCLUDED, SOURCE_ENGINEER_NOT_INCLUDED, SUPER_ADMIN_ALLOWED } from '_/contexts/AuthContext';
